import { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Link as MuiLink, CircularProgress, Alert, Stack, InputAdornment, IconButton, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import OTPInput from '../components/auth/OTPInput';
import { sendForgotPasswordOtp, resetPassword } from '../services/authService';

const PageContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'grid',
  gridTemplateColumns: '1fr',
  '@media (min-width: 1024px)': {
    gridTemplateColumns: '1fr 1fr'
  }
}));

const LeftPanel = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2.5rem 1rem',
  backgroundColor: 'white',
  '@media (min-width: 640px)': {
    padding: '4rem 1.5rem'
  },
  '@media (min-width: 1024px)': {
    padding: '6rem 2rem'
  }
}));

const RightPanel = styled(Box)(() => ({
  display: 'none',
  '@media (min-width: 1024px)': {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#F9FAFB'
  }
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F9FAFB',
    borderRadius: '8px',
    '& input': {
      padding: '16px'
    },
    '& fieldset': {
      borderColor: '#E5E7EB'
    },
    '&:hover fieldset': {
      borderColor: '#D1D5DB'
    },
    '&.Mui-focused': {
  backgroundColor: 'white',
      '& fieldset': {
        borderColor: '#4F46E5',
        borderWidth: '1px'
      }
    },
    '&.Mui-disabled': {
      backgroundColor: '#F3F4F6'
    }
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#9CA3AF',
    opacity: 1
  }
}));

const PrimaryButton = styled(Button)(() => ({
  width: '100%',
  padding: '16px',
  backgroundColor: '#4F46E5',
  color: 'white',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: '#4338CA',
    boxShadow: 'none'
  },
  '&:disabled': {
    backgroundColor: '#D1D5DB',
    color: '#9CA3AF'
  }
}));

interface PasswordStrength {
  score: number;
  label: string;
  color: string;
  percentage: number;
}

const calculatePasswordStrength = (password: string): PasswordStrength => {
  let score = 0;
  
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score === 0 || score === 1) {
    return { score, label: 'Weak', color: '#ef4444', percentage: 25 };
  } else if (score === 2 || score === 3) {
    return { score, label: 'Medium', color: '#f59e0b', percentage: 66 };
  } else {
    return { score, label: 'Strong', color: '#22c55e', percentage: 100 };
  }
};

const detectInputType = (input: string): 'email' | 'phone' => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(input) ? 'email' : 'phone';
};

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2 | 3>(1); // 1: email/phone, 2: OTP, 3: new password
  const [email, setEmail] = useState('');
  const [inputType, setInputType] = useState<'email' | 'phone'>('email');
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);

  // Password strength
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    label: 'Weak',
    color: '#ef4444',
    percentage: 0
  });

  // Password requirements
  const [requirements, setRequirements] = useState({
    minLength: false,
    hasUppercase: false,
    hasNumber: false,
    hasSpecial: false
  });

  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const slides = [
    {
      image: 'https://images.unsplash.com/photo-1614064641938-3bbee52942c7?w=800&auto=format&fit=crop',
      title: 'Secure Your Account',
      description: 'We take security seriously. Reset your password securely and get back to managing your reviews in no time.'
    },
    {
      image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop',
      title: 'Easy Recovery',
      description: 'Reset your password in just a few simple steps. We\'ll send you a verification code to confirm your identity.'
    },
    {
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop',
      title: 'Always Protected',
      description: 'Your business data and customer reviews are always protected with enterprise-grade security.'
    }
  ];

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  // Update password strength and requirements
  useEffect(() => {
    if (newPassword) {
      setPasswordStrength(calculatePasswordStrength(newPassword));
      setRequirements({
        minLength: newPassword.length >= 8,
        hasUppercase: /[A-Z]/.test(newPassword),
        hasNumber: /[0-9]/.test(newPassword),
        hasSpecial: /[^A-Za-z0-9]/.test(newPassword)
      });
    } else {
      setPasswordStrength({ score: 0, label: 'Weak', color: '#ef4444', percentage: 0 });
      setRequirements({
        minLength: false,
        hasUppercase: false,
        hasNumber: false,
        hasSpecial: false
      });
    }
  }, [newPassword]);

  const handleSendCode = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!email.trim()) {
      setError('Please enter your email or phone number');
      return;
    }

    // Detect input type
    const type = detectInputType(email);
    setInputType(type);

    // Basic validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[\d\s-()]+$/;
    
    if (!emailRegex.test(email) && !phoneRegex.test(email)) {
      setError('Please enter a valid email or phone number');
      return;
    }

    // Validate country code for phone numbers
    if (type === 'phone') {
      const trimmedPhone = email.trim();
      if (!trimmedPhone.startsWith('+')) {
        setError('Phone number must include country code (e.g., +1 for US, +91 for India)');
        return;
      }
      
      // Validate that country code has at least 10 digits after +
      const digitsAfterPlus = trimmedPhone.substring(1).replace(/\D/g, '');
      if (digitsAfterPlus.length < 10) {
        setError('Please enter a complete phone number with country code (e.g., +1234567890)');
        return;
      }
    }

    setIsSendingCode(true);

    try {
      // Call forgot password API
      const payload = type === 'email' ? { email } : { phone: email };
      const result = await sendForgotPasswordOtp(payload);
      
      setSuccess(result.message || 'Verification code sent successfully!');
      setStep(2);
      setResendTimer(59);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code. Please try again.');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleVerifyOTP = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    const otpValue = otp.join('');
    if (otpValue.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    // Move to step 3 (password entry)
    setSuccess('Code verified! Now set your new password.');
    setStep(3);
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return;

    setError(null);
    setIsSendingCode(true);

    try {
      // Call forgot password API again
      const payload = inputType === 'email' ? { email } : { phone: email };
      const result = await sendForgotPasswordOtp(payload);
      
      setSuccess(result.message || 'Code resent successfully!');
      setResendTimer(59);
      setOtp(new Array(6).fill(''));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.');
    } finally {
      setIsSendingCode(false);
    }
  };

  const handleResetPassword = async () => {
    setError(null);
    setSuccess(null);

    // Validation
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!Object.values(requirements).every(Boolean)) {
      setError('Please meet all password requirements');
      return;
    }

    setIsLoading(true);

    try {
      // Call reset password API
      const payload = {
        ...(inputType === 'email' ? { email } : { phone: email }),
        otp: otp.join(''),
        new_password: newPassword
      };
      
      const result = await resetPassword(payload);
      
      setSuccess(result.message || 'Password reset successfully! Redirecting to sign in...');
      
      // Redirect to sign in immediately
      navigate('/signin');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSignIn = () => {
    navigate('/signin');
  };

  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;
  const passwordsDontMatch = confirmPassword && newPassword !== confirmPassword;

  return (
    <PageContainer>
      <LeftPanel>
        <Box sx={{ width: '100%', maxWidth: 448, mx: 'auto' }}>
          {/* Header */}
          <Typography
            sx={{
              fontSize: { xs: '1.875rem', sm: '2.25rem' },
              fontWeight: 700,
              lineHeight: 1.2,
              color: '#111827',
              mb: 1
            }}
          >
            {step === 1 ? 'Forgot Your Password?' : step === 2 ? 'Verify Your Identity' : 'Set New Password'}
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: '#6B7280', mb: 4 }}>
            {step === 1 
              ? "No worries! Enter your email or phone number and we'll send you a code to reset your password."
              : step === 2 
              ? 'Enter the verification code we sent to continue.'
              : 'Create a strong password to protect your account and business data.'}
              </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          {step === 1 ? (
            // Step 1: Email/Phone Input
            <Stack spacing={3}>
              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#111827',
                    mb: 1.25
                  }}
                >
                  Email or Phone
              </Typography>
                <StyledTextField
                fullWidth
                  type="text"
                  placeholder="Enter email or phone (with country code, e.g., +1234567890)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                  disabled={isSendingCode}
                />
              </Box>

              <PrimaryButton onClick={handleSendCode} disabled={isSendingCode}>
                {isSendingCode ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Verification Code'
                )}
              </PrimaryButton>

              {/* Back to Sign In */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <MuiLink
                  component="button"
                  onClick={handleBackToSignIn}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: '#6B7280',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    '&:hover': {
                      color: '#4F46E5'
                    }
                  }}
                >
                  <ArrowLeft size={16} />
                  Back to Sign In
                </MuiLink>
              </Box>
            </Stack>
          ) : step === 2 ? (
            // Step 2: OTP Verification
            <Stack spacing={3}>
              <Box>
                <Typography
                sx={{
                  fontSize: '0.875rem',
                    color: '#6B7280',
                    mb: 3,
                    textAlign: 'center'
                  }}
                >
                  We've sent a 6-digit verification code to <strong style={{ color: '#111827' }}>{email}</strong>
                  </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <OTPInput value={otp} onChange={setOtp} length={6} />
                </Box>
                  </Box>

              <PrimaryButton
                    onClick={handleVerifyOTP}
                disabled={otp.join('').length !== 6}
              >
                Verify & Continue
              </PrimaryButton>

              {/* Resend Code */}
                  <Box sx={{ textAlign: 'center' }}>
                <Typography
                  component="span"
                  sx={{ fontSize: '0.875rem', color: '#6B7280', mr: 0.5 }}
                >
                      Didn't receive the code?
                    </Typography>
                    {resendTimer > 0 ? (
                  <Typography
                    component="span"
                    sx={{ fontSize: '0.875rem', color: '#4F46E5', fontWeight: 600 }}
                  >
                    Resend in {resendTimer}s
                      </Typography>
                    ) : (
                      <MuiLink
                        component="button"
                        onClick={handleResendCode}
                        sx={{
                      fontSize: '0.875rem',
                      color: '#4F46E5',
                          fontWeight: 600,
                          cursor: 'pointer',
                          textDecoration: 'none',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          '&:hover': {
                        textDecoration: 'underline',
                        color: '#4338CA'
                          }
                        }}
                      >
                        Resend code
                      </MuiLink>
                    )}
                  </Box>

              {/* Back to Sign In */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <MuiLink
                  component="button"
                  onClick={handleBackToSignIn}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: '#6B7280',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    '&:hover': {
                      color: '#4F46E5'
                    }
                  }}
                >
                  <ArrowLeft size={16} />
                  Back to Sign In
                </MuiLink>
              </Box>
            </Stack>
          ) : (
            // Step 3: New Password Entry
            <Stack spacing={3}>
              {/* New Password Field */}
              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#111827',
                    mb: 1.25
                  }}
                >
                  New Password
                </Typography>
                <StyledTextField
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  autoFocus
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          sx={{ color: '#6B7280', mr: 0.5 }}
                        >
                          {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </Box>

              {/* Password Strength Indicator */}
              {newPassword && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                      Password Strength
                    </Typography>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: passwordStrength.color }}>
                      {passwordStrength.label}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#E5E7EB',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: passwordStrength.color,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>
              )}

              {/* Password Requirements Checklist */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5
                }}
              >
                {[
                  { key: 'minLength', label: 'At least 8 characters' },
                  { key: 'hasUppercase', label: 'One uppercase letter' },
                  { key: 'hasNumber', label: 'At least one number' },
                  { key: 'hasSpecial', label: 'One special character' }
                ].map(({ key, label }) => (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    {requirements[key as keyof typeof requirements] ? (
                      <CheckCircle size={18} color="#22C55E" />
                    ) : (
                      <XCircle size={18} color="#D1D5DB" />
                    )}
                    <Typography
                      sx={{
                        fontSize: '0.875rem',
                        color: requirements[key as keyof typeof requirements] ? '#111827' : '#9CA3AF'
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Confirm Password Field */}
              <Box>
                <Typography
                  component="label"
                  sx={{
                    display: 'block',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: '#111827',
                    mb: 1.25
                  }}
                >
                  Confirm New Password
                </Typography>
                <StyledTextField
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  error={passwordsDontMatch}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          sx={{ color: '#6B7280', mr: 0.5 }}
                        >
                          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: passwordsDontMatch ? '#EF4444' : '#E5E7EB'
                      },
                      '&:hover fieldset': {
                        borderColor: passwordsDontMatch ? '#EF4444' : '#D1D5DB'
                      }
                    }
                  }}
                />
                {passwordsDontMatch && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <XCircle size={16} color="#EF4444" />
                    <Typography sx={{ fontSize: '0.875rem', color: '#EF4444' }}>
                      Passwords do not match
                    </Typography>
                  </Box>
            )}
          </Box>

              {/* Reset Password Button */}
              <PrimaryButton
                onClick={handleResetPassword}
                disabled={isLoading || !passwordsMatch || !Object.values(requirements).every(Boolean)}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </PrimaryButton>

        {/* Back to Sign In */}
        <Box sx={{ textAlign: 'center' }}>
          <MuiLink
            component="button"
            onClick={handleBackToSignIn}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
                    color: '#6B7280',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    padding: 0,
              '&:hover': {
                      color: '#4F46E5'
              }
            }}
          >
                  <ArrowLeft size={16} />
            Back to Sign In
          </MuiLink>
              </Box>
            </Stack>
          )}
        </Box>
      </LeftPanel>

      <RightPanel>
        {/* Carousel */}
        <Box sx={{ width: '100%', maxWidth: 600, position: 'relative' }}>
          {/* Carousel Image */}
          <Box
            component="img"
            src={slides[currentSlide].image}
            alt={slides[currentSlide].title}
            sx={{
              width: '100%',
              height: 400,
              objectFit: 'cover',
              borderRadius: '16px',
              mb: 3
            }}
          />

          {/* Carousel Text */}
          <Box sx={{ textAlign: 'center', px: 2 }}>
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#111827',
                mb: 2
              }}
            >
              {slides[currentSlide].title}
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#6B7280', lineHeight: 1.6 }}>
              {slides[currentSlide].description}
            </Typography>
      </Box>

          {/* Carousel Indicators */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 4 }}>
            {slides.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentSlide(index)}
                sx={{
                  width: currentSlide === index ? 32 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: currentSlide === index ? '#4F46E5' : '#E5E7EB',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              />
            ))}
          </Box>
        </Box>
      </RightPanel>
    </PageContainer>
  );
};

export default ForgotPassword;

